import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

let refreshing = null;
const RETRIABLE_STATUS = [502, 503, 504];
const MAX_COLD_RETRIES = 2;
// Only these auth calls (plus any GET) are safe to auto-retry on cold-start —
// they are idempotent so a retry can't double-apply a mutation.
const RETRY_AUTH_PATHS = ["/auth/login", "/auth/otp/request", "/auth/refresh", "/auth/me"];

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    // Cold-start / gateway resilience: retry safe requests when the backend is
    // waking up (no response, or 502/503/504). Only GETs and /auth/ calls are
    // retried so create/update POSTs are never duplicated.
    if (original) {
      const status = error.response?.status;
      const coldStart = !error.response || RETRIABLE_STATUS.includes(status);
      const method = (original.method || "get").toLowerCase();
      const url = original.url || "";
      const safe = method === "get" || RETRY_AUTH_PATHS.some((p) => url.includes(p));
      if (coldStart && safe) {
        original._coldRetry = original._coldRetry || 0;
        if (original._coldRetry < MAX_COLD_RETRIES) {
          original._coldRetry += 1;
          await new Promise((r) => setTimeout(r, 1200 * original._coldRetry));
          return api(original);
        }
      }
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !original._retry &&
      !original.url.includes("/auth/")
    ) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function formatAuthError(err) {
  const status = err?.response?.status;
  if (!err?.response || RETRIABLE_STATUS.includes(status)) {
    return "The server is waking up. Please wait a few seconds and try again.";
  }
  return (
    formatApiErrorDetail(err.response?.data?.detail) ||
    err.message ||
    "Something went wrong. Please try again."
  );
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
